import React from 'react';
import { DeltaChip } from '../../common/DeltaChip';
import { StateBar } from '../../floor/StateBar';
import { TierBadge } from '../../floor/TierBadge';
import { formatHhMm, formatNumber, formatPercent, formatSignedNumber, formatSignedPts } from '../../../utils/format';
import { floorStates, type FloorRow } from '../../../utils/floor';
interface DrawerFloorProps {
  row: FloorRow;
  dateLabel: string;
}
export function DrawerFloor({
  row,
  dateLabel
}: DrawerFloorProps) {
  const {
    day,
    prev
  } = row;
  const states = floorStates(day);
  const stateRows = [{
    label: 'On Call',
    value: states.onCall,
    swatch: 'bg-accent'
  }, {
    label: 'Waiting',
    value: states.waiting,
    swatch: 'bg-bright/30'
  }, {
    label: 'Ready (total)',
    value: states.ready,
    swatch: 'bg-bright'
  }, {
    label: 'Not Ready',
    value: states.notReady,
    swatch: 'bg-silver/45'
  }];
  const output = [{
    label: 'Calls handled',
    value: day.calls,
    diff: day.calls - prev.calls
  }, {
    label: 'Leads converted',
    value: day.leads,
    diff: day.leads - prev.leads
  }, {
    label: 'Loans to underwriting',
    value: day.loans,
    diff: day.loans - prev.loans
  }];
  return <section className="border-t border-line p-6" aria-labelledby="drawer-floor-heading" data-id="element-519" data-mp="1xto5d9hm2fd6">
      <div className="flex items-baseline justify-between gap-3" data-id="element-520" data-mp="22mqw79v07apo">
        <h3 id="drawer-floor-heading" className="text-sm font-semibold text-ink" data-id="element-521" data-mp="27llgkg8md7bw">
          On the floor
        </h3>
        <p className="text-xs text-muted" data-id="element-522" data-mp="19yv3eg8k22y6">Five9 · {dateLabel}</p>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-4" data-id="element-523" data-mp="1evv0i1nffvd5">
        <div className="rounded-xl border border-line p-3" data-id="element-524" data-mp="1eyqzltb0gq36">
          <p className="text-[11px] text-muted" data-id="element-525" data-mp="1juohw55u46il">Production score</p>
          <p className="mt-1 flex items-center gap-2" data-id="element-526" data-mp="1nx2u46f0080w">
            <span className="font-display text-2xl font-bold leading-none text-ink" data-id="element-527" data-mp="1nynu4bk30j65">{row.score}</span>
            <TierBadge tier={row.tier} data-id="element-528" data-mp="2vamq66t6uoir" />
          </p>
          <DeltaChip className="mt-1.5" value={row.score - row.prevScore} text={`${formatSignedNumber(row.score - row.prevScore)} vs prior day`} data-id="element-529" data-mp="2zbq9x4ecx4p9" />
        </div>
        <div className="rounded-xl border border-line p-3" data-id="element-530" data-mp="109ks8uffi7ca">
          <p className="text-[11px] text-muted" data-id="element-531" data-mp="v7ve35wwtdjz">Focus</p>
          <p className="mt-1 font-display text-2xl font-bold leading-none text-ink" data-id="element-532" data-mp="19863s5mhgm3k">{formatPercent(row.focus)}</p>
          <DeltaChip className="mt-1.5" value={row.focus - row.prevFocus} text={`${formatSignedPts(row.focus - row.prevFocus)} vs prior day`} data-id="element-533" data-mp="148tgxkvm3f0z" />
        </div>
      </div>

      <div className="mt-5" data-id="element-534" data-mp="ghegjxneb7sd">
        <div className="mb-2 flex items-baseline justify-between text-xs" data-id="element-535" data-mp="ciyi8xlv6czn">
          <span className="font-medium text-ink" data-id="element-536" data-mp="q9k3uu2pd118">Floor time</span>
          <span className="text-muted" data-id="element-537" data-mp="qav8ew3z2cte">Logged in {formatHhMm(states.loggedIn)}</span>
        </div>
        <StateBar states={states} size="md" data-id="element-538" data-mp="lhiubfwx5oew" />
        <ul className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1.5" data-id="element-539" data-mp="3ty5akbrskp4l">
          {stateRows.map(s => <li key={s.label} className="flex items-center justify-between gap-2 text-xs" data-id="element-540" data-mp="3p03rvrya2rtn">
              <span className="flex items-center gap-1.5 text-muted" data-id="element-541" data-mp="24hrv82jy7xwx">
                <span className={`h-2 w-2 rounded-sm ${s.swatch}`} aria-hidden data-id="element-542" data-mp="24ff7f8rietby" />
                {s.label}
              </span>
              <span className="font-semibold tabular-nums text-ink" data-id="element-543" data-mp="1vh3hza2dq1l3">{formatHhMm(s.value)}</span>
            </li>)}
          {states.unrecorded > 0 && <li className="flex items-center justify-between gap-2 text-xs" data-id="element-544" data-mp="20dtvpqqpol3c">
              <span className="flex items-center gap-1.5 text-muted" data-id="element-545" data-mp="2o60n0bhky1vw">
                <span className="h-2 w-2 rounded-sm border border-line bg-canvas" aria-hidden data-id="element-546" data-mp="2s7dt8mol473c" />
                Unrecorded
              </span>
              <span className="font-semibold tabular-nums text-ink" data-id="element-547" data-mp="2e734bp6u52kt">{formatHhMm(states.unrecorded)}</span>
            </li>}
        </ul>
      </div>

      <dl className="mt-5 grid grid-cols-3 gap-px overflow-hidden rounded-xl border border-line bg-line" data-id="element-548" data-mp="12rwia7r6v5y9">
        {output.map(o => <div key={o.label} className="bg-surface p-3" data-id="element-549" data-mp="17sk8ir7lfirf">
            <dt className="text-[11px] text-muted" data-id="element-550" data-mp="1n15sk64vd8u4">{o.label}</dt>
            <dd className="mt-0.5 text-base font-bold text-ink" data-id="element-551" data-mp="1n2ql5q7mihpc">{formatNumber(o.value)}</dd>
            <dd data-id="element-552" data-mp="1i8co7jdbc2ik">
              <DeltaChip value={o.diff} text={formatSignedNumber(o.diff)} data-id="element-553" data-mp="1e8cbc35v3zdm" />
            </dd>
          </div>)}
      </dl>

      {row.flags.length > 0 && <ul className="mt-4 space-y-1.5" data-id="element-554" data-mp="19bc3bmpgcmjv">
          {row.flags.map(f => <li key={f.kind} className="flex items-center gap-2 rounded-lg bg-warning/5 px-3 py-2 text-xs" data-id="element-555" data-mp="25ygtdv65gabo">
              <span className="font-semibold text-warning" data-id="element-556" data-mp="21yoe6zm935xc">{f.label}</span>
              <span className="text-muted" data-id="element-557" data-mp="1wy0qonx8d77v">{f.detail}</span>
            </li>)}
        </ul>}
    </section>;
}